import { useState } from 'react';
import { keys, isEmpty } from 'lodash';
import { useSelector } from 'react-redux';

// eslint-disable-next-line import/named
import { getAgreements } from '@flow/libs/oneflow-client/agreements';
import foldersReducer from '@app/reducers/entities/folders';
import { useFilterContext } from '@app/hocs/search/filter/context';
import useClientFeatureFlag from '@app/hooks/use-client-feature-flag';
import { getFoldersByParentId } from '@flow/libs/workspace/folders';
import agreementsReducer from '@app/reducers/entities/agreements';

import { T } from '@app/components/translation';
import { BadgeName } from '@app/components/badges/badge-name';
import EmptyState from '@app/components/empty-state';
import Checkbox from '@app/components/checkbox';
import CircularSpinner from '@app/components/icons/circular-spinner';
import UserGroupsIcon from '@app/components/icons/user-groups';
import { WORKSPACE_ROOT_FOLDER_ID } from '@flow/libs/workspace/folders-constants';
import { useFoldersQueryState } from '@app/components/folder-tree/use-folders-query';
import { useFolderQueryContext } from '@app/contexts/folder-query-context';

import { ExportModalBase } from '.';
import { TooManyRequests } from './helpers/too-many-requests';
import style from './export.module.scss';

type Request = {
  pagination: {
    limit: number;
  };
  normalizeOutput: boolean;
};

type Props = {
  onExport: (onSuccess: () => void, onFailure: (code: number) => void) => void;
  email: string;
  query: ReturnType<typeof agreementsReducer.getQuerySelector>;
  workspace: Oneflow.Workspace;
  folderId: Oneflow.Folder['id'];
  selectedFolder: Oneflow.Folder;
  includeSubfolders: boolean;
  changeCheckboxState: () => void;
};

const ignoredOperators = [
  'positionOperator',
  'tagOperator',
  'folderIds',
];

export const ExportDocumentsModal = ({
  onExport,
  email,
  query,
  workspace,
  folderId,
  selectedFolder,
  includeSubfolders,
  changeCheckboxState,
}: Props) => {
  const [errorStatusCode, setErrorStatusCode] = useState<number | undefined>(undefined);
  const [agreements, setAgreements] = useState<null | Oneflow.Agreement[]>(null);
  const { activeFilters } = useFilterContext();
  const isFolderV2: boolean = useClientFeatureFlag('folder-v2');
  const currentFolder = useSelector((state) =>
    foldersReducer.getFolderSelector(state, {
      id: folderId,
    }),
  ) as Oneflow.Folder;
  const currentSelectedFolder = isFolderV2 ? currentFolder : selectedFolder;

  const folderQueryContext = useFolderQueryContext();
  const { data: folders } = useFoldersQueryState(folderQueryContext?.queryName || '');

  const getWorkspaceAgreements = async (
    request: Request,
    setHasError: (hasError: boolean) => void,
  ) => {
    try {
      const agreementsQuery = await getAgreements(request);
      setAgreements(agreementsQuery.result);
    } catch (error) {
      setAgreements(null);
      setHasError(true);
    }
    return null;
  };

  const onOpen = (setHasError: (hasError: boolean) => void) => {
    const folderIds =
      folderId === WORKSPACE_ROOT_FOLDER_ID
        ? []
        : [
            folderId,
          ];
    const params = {
      ...query.params,
      folderIds: folderIds,
      collectionIds: [
        workspace?.id,
      ],
      includeFolderSubtree: true,
      isExport: true,
    };

    const request = {
      params,
      pagination: {
        limit: 100,
      },
      normalizeOutput: true,
    };

    getWorkspaceAgreements(request, setHasError);
  };

  const renderExportHelpText = () => {
    const filters = keys(activeFilters).filter((key) => !ignoredOperators.includes(key));
    const hasFilters = !isEmpty(filters);

    const name =
      (folderId !== -1 && currentSelectedFolder !== undefined
        ? currentSelectedFolder.name
        : workspace?.name) || '';

    if (name !== '') {
      if (folderId !== -1) {
        if (hasFilters) {
          return (
            <T
              _str="The export will include only filtered documents from folder {name}."
              name={<BadgeName>{name}</BadgeName>}
              _comment="Help text explaining what documents and folders will be exported."
            />
          );
        }

        return (
          <T
            _str="The export will include all documents from folder {name}."
            name={<BadgeName>{name}</BadgeName>}
            _comment="Help text explaining what documents and folders will be exported."
          />
        );
      }

      if (hasFilters) {
        return (
          <T
            _str="The export will include only filtered documents from workspace {name}."
            name={<BadgeName>{name}</BadgeName>}
            _comment="Help text explaining what documents and folders will be exported."
          />
        );
      }

      return (
        <T
          _str="The export will include all documents from workspace {name}."
          name={<BadgeName>{name}</BadgeName>}
          _comment="Help text explaining what documents and folders will be exported."
        />
      );
    }
    if (hasFilters) {
      return (
        <T
          _str="The export will include only filtered documents from all workspaces."
          _comment="Help text explaining what documents and folders will be exported."
        />
      );
    }

    return (
      <T
        _str="The export will include all documents from all workspaces."
        _comment="Help text explaining what documents and folders will be exported."
      />
    );
  };

  const renderSubfoldersCheckbox = () => {
    if (!currentSelectedFolder) {
      return null;
    }

    // After folderV2 is live, we can replace hasChildren with currentSelectedFolder?.childCount
    const hasChildren = getFoldersByParentId(folders, currentSelectedFolder.id, true).length > 0;
    if (!hasChildren || !workspace) {
      return null;
    }

    return (
      <div
        className={style.CheckboxContainer}
        data-testid="subfolders-checkbox"
      >
        <Checkbox
          customClass={style.Checkbox}
          input={{
            checked: includeSubfolders,
            onChange: changeCheckboxState,
          }}
          label={
            <T
              _str="Include documents in subfolders"
              _comment="Used as the label for a checkbox if the user wants to include subfolders"
            />
          }
        />
      </div>
    );
  };

  const renderBody = () => {
    if (agreements === null) {
      return (
        <div
          className={style.LoadingSpinner}
          data-testid="loading-spinner"
        >
          <CircularSpinner />
        </div>
      );
    }

    if (!agreements?.length) {
      return (
        <EmptyState
          icon={<UserGroupsIcon height="33px" />}
          header={
            <T
              _str="No documents to export"
              _comment="Empty state header. Shown in contract export modal when having no contracts."
            />
          }
          content={
            <T
              _str="Please adjust your search criteria and try again."
              _comment="Empty state content. Shown in export modal when having no data."
            />
          }
          defaultStyle
          className={style.NoData}
        />
      );
    }

    return (
      <>
        <p>{renderExportHelpText()}</p>
        {renderSubfoldersCheckbox()}
        <p>
          <T
            _str="The download link will be sent to {userEmail} shortly."
            userEmail={<BadgeName>{email}</BadgeName>}
            _comment="Help text explaining where the email will be sent in the export modal."
          />
        </p>
        <TooManyRequests errorStatusCode={errorStatusCode} />
      </>
    );
  };

  return (
    <ExportModalBase
      tooltip={
        <T
          _str="Export documents"
          _comment="Tooltip text explaining what the export button is for."
        />
      }
      header={
        <T
          _str="Export documents"
          _comment="Confirm modal title for exporting documents."
        />
      }
      onExport={onExport}
      renderBody={renderBody}
      setErrorStatusCode={setErrorStatusCode}
      disabled={!agreements?.length}
      onOpen={onOpen}
    />
  );
};
